import { useRef, useState } from "react";
import { useClickOutside } from "@zapier/design-system";
import { useFocusTrap } from "@zapier/trials/src/hooks/useFocusTrap";

type Props = {
  ariaLabel: string;
  children?: React.ReactNode;
  className?: string;
  onClickOutside: () => void;
};

export const Popover = ({
  ariaLabel,
  children,
  className,
  onClickOutside,
}: Props) => {
  const [previousFocus, setPreviousFocus] = useState<HTMLElement | null>(null);
  const [setClickOutsideNode] = useClickOutside({ onClickOutside });
  const popoverRef = useRef<HTMLDivElement | null>(null);

  useFocusTrap(popoverRef);

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      onClickOutside();
      if (previousFocus) {
        setTimeout(() => {
          previousFocus.focus();
        });
      }
    }
  };

  const onFocusIn = (event: React.FocusEvent) => {
    const relatedTarget = event.relatedTarget as HTMLElement;
    if (relatedTarget && !previousFocus) setPreviousFocus(relatedTarget);
  };

  return (
    <div
      ref={(element) => {
        setClickOutsideNode(element);
        popoverRef.current = element;
      }}
      className={className}
      aria-label={ariaLabel}
      aria-modal="true"
      role="dialog"
      onKeyDownCapture={onKeyDown}
      onFocusCapture={onFocusIn}
    >
      {children}
    </div>
  );
};
